import { useEffect, useState, type FormEvent } from 'react';
import { errorText } from '../api';
import { formatDay, todayKey } from '../format';
import { adminApi, type Brand, type Override } from './api';

/** Einzelne Tage: sperren (Urlaub, Messe – gilt für alle Terminarten) oder zusätzlich freigeben. */
export function DaysPage() {
  const [overrides, setOverrides] = useState<Override[] | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [kind, setKind] = useState<'blocked' | 'open'>('blocked');
  const [day, setDay] = useState(todayKey());
  const [wholeDay, setWholeDay] = useState(true);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('12:00');
  const [eventTypeId, setEventTypeId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.listOverrides().then((r) => setOverrides(r.overrides)).catch((e) => setError(errorText(e)));
    adminApi.listBrands().then((r) => setBrands(r.brands)).catch(() => undefined);
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const times = kind === 'open' || !wholeDay ? { start, end } : { start: null, end: null };
      const created = await adminApi.createOverride({ day, kind, ...times, eventTypeId: kind === 'open' && eventTypeId ? eventTypeId : null, note: note || null });
      setOverrides([...(overrides ?? []), created].sort((a, b) => a.day.localeCompare(b.day) || (a.start ?? '').localeCompare(b.start ?? '')));
      setNote('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await adminApi.deleteOverride(id).catch(() => undefined);
    setOverrides((overrides ?? []).filter((o) => o.id !== id));
  }

  return (
    <>
      <div className="admin-head">
        <div>
          <h1>Einzelne Tage</h1>
          <p className="muted">Urlaub, Messe oder ein zusätzlicher Samstag – Ausnahmen vom Wochenraster.</p>
        </div>
      </div>
      <div className="admin-grid">
        <form className="card" onSubmit={create}>
          <h2 className="card__title">Ausnahme eintragen</h2>
          <div className="segmented">
            <button type="button" className={kind === 'blocked' ? 'is-active' : ''} onClick={() => setKind('blocked')}>
              Sperren
            </button>
            <button type="button" className={kind === 'open' ? 'is-active' : ''} onClick={() => setKind('open')}>
              Zusätzlich freigeben
            </button>
          </div>
          <div className="field">
            <label htmlFor="ov-day">Tag</label>
            <input id="ov-day" type="date" required min={todayKey()} value={day} onChange={(e) => setDay(e.target.value)} />
          </div>
          {kind === 'blocked' && (
            <label className="choice">
              <input type="checkbox" checked={wholeDay} onChange={(e) => setWholeDay(e.target.checked)} />
              <span>Ganzer Tag</span>
            </label>
          )}
          {(kind === 'open' || !wholeDay) && (
            <div className="grid-2">
              <div className="field">
                <label htmlFor="ov-start">Von</label>
                <input id="ov-start" type="time" step={300} required value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="ov-end">Bis</label>
                <input id="ov-end" type="time" step={300} required value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
          )}
          {kind === 'open' && (
            <div className="field">
              <label htmlFor="ov-type">Für welche Terminart</label>
              <select id="ov-type" value={eventTypeId} onChange={(e) => setEventTypeId(e.target.value)}>
                <option value="">Alle Terminarten</option>
                {brands.flatMap((b) => b.eventTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {b.name}: {t.title}
                  </option>
                )))}
              </select>
            </div>
          )}
          {kind === 'blocked' && <p className="muted small">Sperren gilt für alle Terminarten.</p>}
          <div className="field">
            <label htmlFor="ov-note">Notiz für dich</label>
            <input id="ov-note" type="text" placeholder="z. B. Messe Hannover" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <div className="error">{error}</div>}
          <div>
            <button type="submit" className="button" disabled={busy}>
              Eintragen
            </button>
          </div>
        </form>
        <div className="card">
          <h2 className="card__title">Kommende Ausnahmen</h2>
          {overrides === null && <p className="muted small">Lade …</p>}
          {overrides?.length === 0 && <p className="muted small">Keine Ausnahmen – es gilt das Wochenraster.</p>}
          <ul className="plain-list plain-list--rows">
            {overrides?.map((o) => (
              <li key={o.id}>
                <div>
                  <strong>{formatDay(o.day)}</strong>
                  <div className="small">
                    <span className={o.kind === 'blocked' ? 'tag tag--blocked' : 'tag tag--open'}>{o.kind === 'blocked' ? 'gesperrt' : 'frei'}</span>{' '}
                    {o.start ? `${o.start}–${o.end} Uhr` : 'ganzer Tag'}
                    {o.kind === 'open' && <> · {o.eventTypeTitle ?? 'alle Terminarten'}</>}
                    {o.note && <span className="muted"> · {o.note}</span>}
                  </div>
                </div>
                <button type="button" className="link-button" onClick={() => void remove(o.id)}>
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
